import { formatDate } from '@seen/core/dates'
import type { Intervention } from '@seen/core/models/care-plans/intervention'
import { IconIntervention } from '@seen/ui'
import type { JSX } from 'react'
import { PractitionerName } from '#src/components/PractitionerName.tsx'

/* Name, when it was created, and who owns it — nothing else. The created date
 * carries the ordering, which is why it is here rather than the status: a row
 * saying "Completed" told a clinician picking an intervention to write about
 * something they had not asked.
 *
 * Takes the model rather than a reference, unlike the option rows that render
 * from cache: the picker has already searched these, so re-reading each one
 * would issue a request per row for data it is holding. `label` comes in
 * already derived, because the picker needs the same string to sort and to
 * build keywords and deriving it parses the intervention's description. */
export function InterventionOption({
  intervention,
  label,
}: {
  intervention: Intervention
  label: string
}): JSX.Element {
  const { created, author } = intervention
  return (
    <div className="flex items-center gap-2">
      <div className="p-0.5">
        <IconIntervention className="size-4 shrink-0" />
      </div>
      <span>
        {label}
        <span className="text-muted-foreground">
          {created && <> · {formatDate(new Date(created))}</>}
          {/* Roughly a third of interventions have no owner, so the row has to
              read without one rather than trail a separator. */}
          {author && (
            <>
              {' · '}
              <PractitionerName practitioner={author} />
            </>
          )}
        </span>
      </span>
    </div>
  )
}
